<!-- src/apps/secret/components/dashboard/DashboardReceiptsTable.vue -->

<script setup lang="ts">
  import { peekAttachmentFragment } from '@/apps/secret/composables/useAttachmentFragmentStore';
  import { useClipboard } from '@/shared/composables/useClipboard';
  import { useRecentSecrets, type RecentSecretRecord } from '@/shared/composables/useRecentSecrets';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { formatDisplayDateTime } from '@/utils/format';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  /**
   * Receipts ledger for the authenticated dashboard (Figma Make, ago/2026):
   * a mono grid — status dot, created, expires, subject, delivery, id, actions.
   *
   * The dashboard container is max-w-4xl (~864px of content), so the grid is
   * sized to fit that width WITHOUT horizontal scroll: the constant "AES-256"
   * cipher column was dropped (it carries no per-row information and still shows
   * on the receipt page), and ENVIO is an icon-only channel glyph. Only SUBJECT
   * is fluid, absorbing the leftover width and truncating with a hover title.
   *
   * Data comes from useRecentSecrets, the same source RecentSecretsTable uses,
   * so authenticated users still read the API-backed list (and guests the
   * session-storage one) — only the presentation is new.
   */

  const { t } = useI18n();

  const { records, hasRecords, workspaceMode, toggleWorkspaceMode, fetch, refreshStatuses, isAuthenticated } =
    useRecentSecrets();

  // Surface the total count so the parent tab can show a badge.
  const emit = defineEmits<{ (e: 'count', total: number): void }>();
  watch(records, (r) => emit('count', r.length), { immediate: true });

  const bootstrapStore = useBootstrapStore();
  const { link_host: linkHost } = storeToRefs(bootstrapStore);

  const REFRESH_THROTTLE_MS = 5000;
  let lastRefreshTime = 0;

  // Background refresh when the tab regains focus, mirroring
  // RecentSecretsTable: errors stay silent because the user did not ask.
  const handleVisibilityChange = async () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastRefreshTime < REFRESH_THROTTLE_MS) return;
    lastRefreshTime = now;
    try {
      if (isAuthenticated.value) {
        await fetch({ silent: true });
      } else {
        await refreshStatuses({ silent: true });
      }
    } catch (e) {
      console.debug('Background refresh failed:', e);
    }
  };

  onMounted(async () => {
    await fetch();
    if (!isAuthenticated.value) await refreshStatuses();
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

  // Manual, grid-only refresh: re-queries just the message list so the user can
  // catch a secret that was revealed/burned/expired without reloading the page.
  // Authenticated users re-hit the receipts API (store.fetchList); guests
  // re-check their local receipts' server status. Not silent — a manual action
  // should surface an error if the request fails. A dedicated ref (rather than
  // the shared isLoading) drives the button so background refreshes never spin
  // it.
  const isRefreshing = ref(false);
  const refreshList = async () => {
    if (isRefreshing.value) return;
    isRefreshing.value = true;
    try {
      if (isAuthenticated.value) {
        await fetch();
      } else {
        await refreshStatuses();
      }
    } finally {
      isRefreshing.value = false;
    }
  };

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  type RowStatus = 'active' | 'revealed' | 'burned' | 'expired';

  const statusOf = (record: RecentSecretRecord): RowStatus => {
    if (record.isBurned) return 'burned';
    if (record.isRevealed) return 'revealed';
    if (record.isExpired) return 'expired';
    return 'active';
  };

  const STATUS_LABEL: Record<RowStatus, string> = {
    active: 'receipt_terminal.status_active',
    revealed: 'web.STATUS.revealed',
    burned: 'web.STATUS.burned',
    expired: 'web.STATUS.expired',
  };

  const STATUS_DOT: Record<RowStatus, string> = {
    active: 'bg-emerald-500',
    revealed: 'bg-red-500',
    burned: 'bg-amber-500',
    expired: 'bg-day-ink-faint dark:bg-night-ink-faint',
  };

  const STATUS_TEXT: Record<RowStatus, string> = {
    active: 'text-emerald-600 dark:text-emerald-400',
    revealed: 'text-red-600 dark:text-red-400',
    burned: 'text-amber-600 dark:text-amber-400',
    expired: 'text-day-ink-faint dark:text-night-ink-faint',
  };

  // ENVIO column: the delivery channel picked at creation. Legacy receipts
  // predate the field and fall back to "link" (the historical default).
  type DeliveryMethod = 'link' | 'email' | 'whatsapp' | 'telegram';

  const DELIVERY_LABEL: Record<DeliveryMethod, string> = {
    link: 'dashboard.delivery_link',
    email: 'dashboard.delivery_email',
    whatsapp: 'dashboard.delivery_whatsapp',
    telegram: 'dashboard.delivery_telegram',
  };

  const deliveryOf = (record: RecentSecretRecord): DeliveryMethod =>
    record.deliveryMethod ?? 'link';

  // A destroyed secret has no meaningful expiry left; the mock renders an
  // em dash for those rows.
  const expiresAt = (record: RecentSecretRecord) => {
    if (record.isDestroyed) return '—';
    // Show date AND time: short TTLs (e.g. 1 hour) expire the same day, so a
    // date-only value would read identically to "created" and hide the real
    // expiry moment. Match the "created" column's date+time format.
    return formatDisplayDateTime(new Date(record.createdAt.getTime() + record.ttl * 1000));
  };

  // The share URL is only meaningful while the secret is still live; once it is
  // revealed, burned, or expired the link is dead, so "copy" is offered on
  // active rows only. Mirrors SecretComposer/SecretLinksTableRow URL shape.
  const secretUrlOf = (record: RecentSecretRecord) => {
    const domain = record.shareDomain || linkHost.value;
    if (!domain || !record.secretExtid) return '';
    // Append the E2EE attachment fragment when we still hold it (this tab): the
    // keys live only in the fragment, so a copied link without it can't decrypt.
    const frag = record.extid ? peekAttachmentFragment(record.extid) : '';
    return `https://${domain}/secret/${record.secretExtid}${frag ? `#${frag}` : ''}`;
  };

  // Cap the ledger to the most recent N so the dashboard stays scannable; the
  // full history lives on its own page. `records` is already newest-first.
  const MAX_ROWS = 10;

  const rows = computed(() =>
    records.value.slice(0, MAX_ROWS).map((record) => {
      const status = statusOf(record);
      const secretUrl = secretUrlOf(record);
      const delivery = deliveryOf(record);
      const attachments = record.attachments ?? [];
      return {
        key: record.id,
        status,
        statusLabel: t(STATUS_LABEL[status]),
        dotClass: STATUS_DOT[status],
        textClass: STATUS_TEXT[status],
        created: formatDisplayDateTime(record.createdAt),
        expires: expiresAt(record),
        // ASSUNTO: creator-supplied subject (may be empty); truncated in the cell.
        subject: record.subject ?? '',
        // ENVIO: the delivery channel + its localized label.
        delivery,
        deliveryLabel: t(DELIVERY_LABEL[delivery]),
        // DESTINO: the email the secret was sent to (link/whatsapp have none —
        // WhatsApp numbers are not persisted server-side).
        recipient: record.recipient ?? '',
        shortid: record.shortid,
        secretUrl,
        canCopy: status === 'active' && !!secretUrl,
        receiptPath: record.extid ? `/receipt/${record.extid}` : '',
        // ARQUIVOS: name-only attachment list (no download credential here).
        attachments,
        attachmentCount: attachments.length,
      };
    })
  );

  // Per-row "copied" feedback: useClipboard's single isCopied ref can't tell
  // rows apart, so the copied row's key is tracked here and cleared after 2s.
  // There is no server endpoint to re-send a secret's notification email
  // (only account verification/email-change confirmations resend), so the row
  // offers what actually exists: copy the live link, and open the receipt.
  const { copyToClipboard } = useClipboard();
  const copiedId = ref<string | null>(null);
  let copyTimer: ReturnType<typeof setTimeout> | null = null;

  const copyLink = async (row: { key: string; secretUrl: string }) => {
    if (!row.secretUrl) return;
    const ok = await copyToClipboard(row.secretUrl);
    if (!ok) return;
    copiedId.value = row.key;
    if (copyTimer) clearTimeout(copyTimer);
    copyTimer = setTimeout(() => {
      copiedId.value = null;
      copyTimer = null;
    }, 2000);
  };

  onUnmounted(() => {
    if (copyTimer) clearTimeout(copyTimer);
  });
</script>

<template>
  <section aria-labelledby="dashboard-receipts-heading">
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <h2
          id="dashboard-receipts-heading"
          class="font-mono text-[13px] font-semibold tracking-[-0.01em] text-day-ink dark:text-night-ink">
          {{ t('dashboard.messages_title') }}
        </h2>
        <span
          class="border border-day-line-2 bg-day-100 px-2 py-0.5 font-mono text-[9px] tracking-[0.06em] text-day-ink-faint dark:border-white/[0.13] dark:bg-night-200 dark:text-night-ink-faint">
          {{ rows.length }}
        </span>
        <!-- Grid-only refresh: re-queries the list without reloading the page.
             Spins + disables while in flight; the label rides on aria only. -->
        <button
          type="button"
          class="dashboard-control inline-flex size-6 items-center justify-center border border-day-line-2 text-day-ink-faint transition-colors duration-150 hover:bg-day-100 hover:text-day-ink focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.13] dark:text-night-ink-faint dark:hover:bg-night-200 dark:hover:text-night-ink"
          :disabled="isRefreshing"
          :aria-label="t('dashboard.refresh_list')"
          :title="t('dashboard.refresh_list')"
          data-testid="dashboard-receipts-refresh"
          @click="refreshList">
          <svg
            class="size-3.5"
            :class="isRefreshing ? 'animate-spin' : ''"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
        </button>
      </div>

      <label
        class="flex cursor-pointer items-center gap-2"
        :title="t('web.secrets.workspace_mode_description')">
        <input
          type="checkbox"
          :checked="workspaceMode"
          class="size-3.5 rounded-none border-day-line-2 text-brand-600 focus:ring-brand-500 dark:border-white/[0.13] dark:bg-night-200"
          data-testid="dashboard-workspace-mode"
          @change="toggleWorkspaceMode()" />
        <span
          class="font-mono text-[10px] tracking-[0.04em] text-day-ink-faint dark:text-night-ink-faint">
          {{ t('web.secrets.workspace_mode') }}
        </span>
      </label>
    </div>

    <!-- empty state -->
    <div
      v-if="!hasRecords"
      class="flex flex-col items-center gap-3 border border-day-line-2 bg-day px-6 py-12 dark:border-white/[0.13] dark:bg-night-100"
      data-testid="dashboard-receipts-empty">
      <svg
        class="size-7 text-day-line-2 dark:text-white/20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span
        class="font-mono text-[11px] tracking-[0.06em] text-day-ink-faint dark:text-night-ink-faint">
        {{ t('dashboard.receipts_empty') }}
      </span>
    </div>

    <!-- ledger -->
    <div
      v-else
      class="overflow-x-auto border border-day-line-2 bg-day shadow-[0_2px_8px_rgba(13,21,55,0.04)] dark:border-white/[0.13] dark:bg-night-100 dark:shadow-none"
      data-testid="dashboard-receipts-table">
      <!--
        Fluid, fit-to-container grid. Every column but SUBJECT is a fixed narrow
        width; SUBJECT (no width) soaks up the remaining space and truncates.
        The fixed widths sum to well under the dashboard's ~864px content box,
        so no min-width is forced and there is no horizontal scroll on desktop
        — overflow-x-auto on the wrapper is only a safety net for very narrow
        (mobile) viewports. 88px for STATUS rather than the mock's 80: our
        status words are the real ones ("REVELADO", "QUEIMADO"), which need the
        extra room before the dot+label would spill past the hairline.
      -->
      <!-- table-auto + whitespace-nowrap: every column grows to fit its content
           (full email / phone / filename, never truncated) and the wrapper
           scrolls horizontally instead of squeezing. -->
      <table class="w-full min-w-full table-auto border-collapse text-left">
        <thead>
          <tr class="border-b-2 border-day-line-2 bg-day-100 dark:border-white/[0.18] dark:bg-night-200">
            <th
              v-for="(head, index) in [
                { key: 'dashboard.col_status', extra: '' },
                { key: 'dashboard.col_created', extra: '' },
                { key: 'dashboard.col_expires', extra: '' },
                { key: 'dashboard.col_subject', extra: '' },
                { key: 'dashboard.col_delivery', extra: 'text-center' },
                { key: 'dashboard.col_recipient', extra: '' },
                { key: 'dashboard.col_files', extra: '' },
                { key: 'dashboard.col_id', extra: '' },
                { key: 'dashboard.col_actions', extra: 'text-right' },
              ]"
              :key="head.key"
              scope="col"
              class="h-9 px-4 font-mono text-[11px] font-semibold tracking-[0.08em] whitespace-nowrap text-day-ink-muted uppercase dark:text-night-ink-muted"
              :class="[
                index < 8 ? 'border-r border-day-line dark:border-white/[0.13]' : '',
                head.extra,
              ]">
              {{ t(head.key) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.key"
            class="h-11 transition-colors duration-100 hover:bg-day-100 dark:hover:bg-night-200"
            :class="
              index < rows.length - 1 ? 'border-b border-day-line dark:border-white/[0.13]' : ''
            ">
            <td
              class="border-r border-day-line px-3.5 py-2.5 dark:border-white/[0.13]"
              :data-status="row.status">
              <span class="flex items-center gap-1.5">
                <span
                  class="inline-block size-[5px] shrink-0 rounded-full"
                  :class="row.dotClass"
                  aria-hidden="true"></span>
                <span
                  class="font-mono text-[9px] tracking-[0.06em] whitespace-nowrap uppercase"
                  :class="row.textClass">
                  {{ row.statusLabel }}
                </span>
              </span>
            </td>
            <td
              class="border-r border-day-line px-4 py-2.5 font-mono text-[11px] whitespace-nowrap text-day-ink-muted dark:border-white/[0.13] dark:text-night-ink-muted"
              :title="row.created">
              {{ row.created }}
            </td>
            <td
              class="border-r border-day-line px-4 py-2.5 font-mono text-[11px] whitespace-nowrap text-day-ink-muted dark:border-white/[0.13] dark:text-night-ink-muted"
              :title="row.expires">
              {{ row.expires }}
            </td>
            <!-- ASSUNTO: subject; wraps to a sensible max so a long one doesn't
                 blow out the row, full value on hover. -->
            <td
              class="max-w-[280px] truncate border-r border-day-line px-4 py-2.5 font-mono text-[11px] text-day-ink dark:border-white/[0.13] dark:text-night-ink"
              :title="row.subject || undefined"
              :data-testid="`dashboard-receipt-subject-${row.key}`">
              {{ row.subject || '—' }}
            </td>
            <!-- ENVIO: delivery channel as an icon + label so it is legible at a
                 glance (link = copy, plus email / whatsapp). -->
            <td
              class="border-r border-day-line px-3.5 py-2.5 dark:border-white/[0.13]"
              :data-delivery="row.delivery"
              :data-testid="`dashboard-receipt-delivery-${row.key}`">
              <span
                class="flex items-center justify-center gap-1.5 text-day-ink dark:text-night-ink"
                role="img"
                :title="row.deliveryLabel"
                :aria-label="row.deliveryLabel">
                <svg
                  class="size-4 shrink-0"
                  viewBox="0 0 24 24"
                  aria-hidden="true">
                  <template v-if="row.delivery === 'email'">
                    <g
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <rect
                        x="2"
                        y="4"
                        width="20"
                        height="16"
                        rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </g>
                  </template>
                  <path
                    v-else-if="row.delivery === 'whatsapp'"
                    fill="currentColor"
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885" />
                  <path
                    v-else-if="row.delivery === 'telegram'"
                    fill="currentColor"
                    d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  <path
                    v-else
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <span class="font-mono text-[9px] tracking-[0.04em] uppercase">{{ row.deliveryLabel }}</span>
              </span>
            </td>
            <!-- DESTINO: the email the secret was sent to (link/whatsapp have no
                 server-side destination; WhatsApp numbers are not persisted). -->
            <td
              class="border-r border-day-line px-4 py-2.5 font-mono text-[11px] whitespace-nowrap text-day-ink dark:border-white/[0.13] dark:text-night-ink"
              :data-testid="`dashboard-receipt-recipient-${row.key}`">
              {{ row.recipient || '—' }}
            </td>
            <!-- ARQUIVOS: count badge + full names (name-only; NO download here —
                 the server exposes no attachment id on this surface). -->
            <td
              class="border-r border-day-line px-4 py-2.5 whitespace-nowrap dark:border-white/[0.13]"
              :data-testid="`dashboard-receipt-files-${row.key}`">
              <span
                v-if="row.attachmentCount"
                class="flex items-center gap-1.5 text-day-ink dark:text-night-ink">
                <span
                  class="inline-flex min-w-4 items-center justify-center gap-0.5 rounded-full bg-brand-500 px-1 py-0.5 text-[9px] font-bold text-white">
                  <svg
                    class="size-2.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true">
                    <path
                      d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  {{ row.attachmentCount }}
                </span>
                <span class="font-mono text-[11px]">{{ row.attachments.map((a) => a.filename).join(', ') }}</span>
              </span>
              <span
                v-else
                class="font-mono text-[11px] text-day-ink-faint dark:text-night-ink-faint">—</span>
            </td>
            <td
              class="border-r border-day-line px-4 py-2.5 font-mono text-[11px] whitespace-nowrap text-day-ink-muted dark:border-white/[0.13] dark:text-night-ink-muted">
              {{ row.shortid }}
            </td>
            <td class="px-4 py-2.5 whitespace-nowrap">
              <div class="flex items-center justify-end gap-3">
                <button
                  v-if="row.canCopy"
                  type="button"
                  class="font-mono text-[9px] tracking-[0.06em] uppercase transition-colors duration-100 focus-visible:underline focus-visible:outline-none"
                  :class="
                    copiedId === row.key
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-400'
                  "
                  :data-testid="`dashboard-receipt-copy-${row.key}`"
                  :aria-label="t('dashboard.copy_link_aria')"
                  @click="copyLink(row)">
                  {{ copiedId === row.key ? t('dashboard.copied') : t('dashboard.copy') }}
                </button>
                <router-link
                  v-if="row.receiptPath"
                  :to="row.receiptPath"
                  class="font-mono text-[9px] tracking-[0.06em] text-day-ink-muted uppercase transition-colors duration-100 hover:text-day-ink focus-visible:underline focus-visible:outline-none dark:text-night-ink-muted dark:hover:text-night-ink"
                  :data-testid="`dashboard-receipt-open-${row.key}`"
                  :aria-label="t('dashboard.open_receipt_aria')">
                  {{ t('dashboard.action_open') }}
                </router-link>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
  /* See SecretComposer.vue: a leaked, unlayered `:focus { outline: 2px solid
     currentColor }` from the reveal pages outranks Tailwind's utilities layer,
     so a plain mouse click on the refresh trigger would draw a hard box the
     mock never had. Neutralise it; the button keeps its `focus-visible:ring-*`
     utility for keyboard users. */
  .dashboard-control:focus {
    outline: none;
  }
</style>
